// src/components/card/ConsumptionChart.tsx

import {
  CalendarTodayTwoTone,
  InfoTwoTone,
  TroubleshootTwoTone,
} from '@mui/icons-material'
import {
  Avatar,
  Box,
  Card,
  CardContent,
  CardHeader,
  Collapse,
  Divider,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Popover,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import { ComponentProps, ReactNode, useEffect, useState } from 'react'
import Chart from 'react-google-charts'
import { useNavigate } from 'react-router-dom'

import { FolderId, SensorRecord } from '@/types/domain'

import { ChartTypeSelector, HStack } from '..'
import { currentMonth, getStorage, sensorUnit, setStorage } from '../../api'
import { useFolderSensors } from '../../hooks'

type GranularitySelectorProps = {
  data?: { label: string; value: string; disabled: boolean }[]
} & ComponentProps<typeof ToggleButtonGroup>

const GranularitySelector = ({
  data = [
    { label: 'jährlich', value: 'decadly', disabled: false },
    { label: 'monatlich', value: 'yearly', disabled: false },
    { label: 'täglich', value: 'monthly', disabled: false },
    { label: 'stündlich', value: 'daily', disabled: false },
  ],
  ...rest
}: GranularitySelectorProps) => (
  <ToggleButtonGroup {...rest}>
    {data.map(item => (
      <ToggleButton
        disabled={item.disabled}
        key={item.value}
        value={item.value}
      >
        <CalendarTodayTwoTone sx={{ mr: 1 }} /> {item.label}
      </ToggleButton>
    ))}
  </ToggleButtonGroup>
)

const MediumIcon = ({ ...rest }) => (
  <Avatar>
    <Box alt={''} component={'img'} {...rest} />
  </Avatar>
)

type Props = {
  children: ReactNode
  hasMainSensor: boolean
  id: FolderId
  options: {
    icon: string
    name: string
    type: string
  }
  PAGE_CONFIG?: string
}

export const ConsumptionChart = ({
  children: data,
  hasMainSensor,
  id,
  options,
  PAGE_CONFIG = 'settings_performance',
}: Props) => {
  const theme = useTheme()
  const correlation = theme.palette.correlation.main
  const navigate = useNavigate()

  const [sensors, setSensors] = useState([])

  const fSensors: SensorRecord[] = useFolderSensors(id)

  const { icon, name, type } = options

  const [config, setConfig] = useState(getStorage(PAGE_CONFIG))

  const noData = 'Keine Werte vorhanden'
  const periods = ['decade', 'year', 'month', 'day']
  const [anchorEl, setAnchorEl] = useState(null)

  periods.map(period => {
    if (data[period]) {
      if (data[period].length < 2) data[period].push([noData, 0, ''])
    } else {
      // package not booked
      data[period] = []
      data[period].push([currentMonth, sensorUnit(type), ''])
      data[period].push([noData, 0, ''])
    }

    return null
  })

  useEffect(() => setStorage(PAGE_CONFIG, config), [config, PAGE_CONFIG])

  if (!config.chartType)
    setConfig(prev => ({ ...prev, chartType: 'ColumnChart' }))

  if (!config.view) setConfig(prev => ({ ...prev, view: 'daily' }))

  // handler

  const changeView = (event, view) => {
    if (view !== null) setConfig(prev => ({ ...prev, view }))
  }

  const changeChartType = (event, chartType) => {
    if (chartType !== null) setConfig(prev => ({ ...prev, chartType }))
  }

  const gotoDetailPage = () => navigate(`/folder/${id}/detail/${type}`)

  const onToggleOverview = event =>
    setAnchorEl(anchorEl ? null : event.currentTarget)

  useEffect(() => {
    const getNamesByType = type =>
      fSensors
        ?.filter(f => f.sensor_target === 'performance')
        ?.filter(f => f.sensor_type === type || f.sub_sensor_target === type)
        ?.filter(f =>
          type === 'energy'
            ? f.sub_sensor_target !== 'gas' && f.sub_sensor_target !== 'water'
            : type === 'water'
              ? f.sub_sensor_target !== 'gas' &&
                f.sub_sensor_target !== 'energy'
              : type === 'gas'
                ? f.sub_sensor_target !== 'energy' &&
                  f.sub_sensor_target !== 'water'
                : true
        )
        .map(s => s.name)
        .sort((a, b) => a.localeCompare(b)) || []

    setSensors(getNamesByType(type))
  }, [fSensors, hasMainSensor, type])

  return (
    <Card variant={'performance'}>
      <CardHeader
        action={
          <HStack>
            <GranularitySelector
              exclusive
              onChange={changeView}
              sx={{ mx: 1 }}
              value={config.view}
            />

            <ChartTypeSelector
              exclusive
              onChange={changeChartType}
              sx={{ mx: 1 }}
              value={config.chartType}
            />

            <Tooltip title={`Details ${name}`}>
              <IconButton onClick={gotoDetailPage}>
                <TroubleshootTwoTone />
              </IconButton>
            </Tooltip>
          </HStack>
        }
        avatar={<MediumIcon src={icon} />}
        title={name}
        titleTypographyProps={{ variant: 'h6' }}
        variant={'feeling'}
      />

      <CardContent sx={{ background: '#EEE', position: 'relative' }}>
        {[
          {
            data: data['decade'],
            format: 'YYYY',
            granularity: 'decadly',
            title: 'Verbrauch in den letzten Jahren',
          },
          {
            data: data['year'],
            format: 'MM/YYYY',
            granularity: 'yearly',
            title: 'Verbrauch in den letzten 12 Monaten',
          },
          {
            data: data['month'],
            format: 'dd.MM.',
            granularity: 'monthly',
            title: 'Verbrauch in den letzten 30 Tagen',
          },
          {
            data: data['day'],
            format: 'HH:mm',
            granularity: 'daily',
            title: 'Verbrauch in den letzten 24 Stunden',
          },
        ].map(item => (
          <Collapse
            key={item.granularity}
            in={config.view === item.granularity}
          >
            <Chart
              chartLanguage={'de_DE'}
              chartType={config.chartType}
              data={item.data}
              height={300}
              loader={<LinearProgress />}
              options={{
                backgroundColor: '#EEEEEE',
                chartArea: { height: '80%', width: '85%' },
                colors: [correlation],
                curveType: 'function',
                hAxis: { format: item.format, textStyle: { fontSize: 10 } },
                legend: 'none',
                title: item.title,
                vAxis: {
                  format: `#,###.## ${sensorUnit(type)}`,
                  viewWindow: { min: 0 },
                  0: { baseline: 0 },
                },
              }}
            />
          </Collapse>
        ))}

        <Tooltip title={'Messpunkte'}>
          <IconButton
            onClick={onToggleOverview}
            sx={{ position: 'absolute', top: 20, right: 20 }}
          >
            <InfoTwoTone />
          </IconButton>
        </Tooltip>
      </CardContent>

      <Popover
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        onClose={() => setAnchorEl(false)}
        open={Boolean(anchorEl)}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Stack>
          <Typography sx={{ fontWeight: 700, px: 2, py: 1 }}>
            Messpunkte
          </Typography>

          <Divider />

          <List sx={{ p: 1 }}>
            {sensors.map(s => (
              <ListItem key={s}>
                <ListItemText primary={s} />
              </ListItem>
            ))}
          </List>
        </Stack>
      </Popover>
    </Card>
  )
}
